import type { UseFormReturn } from "react-hook-form";
import { useFieldArray, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import CreatableSelect from "react-select/creatable";
import type { ProviderFormData } from "../hooks/useProviderForm";
import type { ThemeClasses } from "../types";
import { ADAPTER_OPTIONS, AUTH_STRATEGY_OPTIONS } from "../types";
import { getCustomSelectStyles } from "../../../../../admin/shared";

interface ProviderFormModalProps {
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
  form: UseFormReturn<ProviderFormData>;
  loadingSubmit: boolean;
  onClose: () => void;
  onSubmit: (data: ProviderFormData) => Promise<void>;
  isEdit: boolean;
  hasFormChanges: () => boolean;
}

export default function ProviderFormModal({
  themeClasses,
  isDarkMode,
  form,
  loadingSubmit,
  onClose,
  onSubmit,
  isEdit,
  hasFormChanges,
}: ProviderFormModalProps) {
  const { t } = useTranslation("resellerAdmin");
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  const creds = useFieldArray({ control, name: "credentials_schema" });
  const endpoints = useFieldArray({ control, name: "endpoints_extra" });

  const active = watch("active");
  const supportsDiscovery = watch("supports_discovery");

  const inputClass = `w-full px-3 py-2.5 rounded-xl border ${themeClasses.border} ${themeClasses.bg} ${themeClasses.text} text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50`;
  const labelClass = `block text-sm font-medium ${themeClasses.text} mb-1`;

  const selectStyles = getCustomSelectStyles(isDarkMode, false);
  const adapterOptions = ADAPTER_OPTIONS.map((o) => ({
    value: o.value,
    label: t(`providers.${o.labelKey}`),
  }));
  const authStrategyOptions = AUTH_STRATEGY_OPTIONS.map((o) => ({
    value: o.value,
    label: t(`providers.${o.labelKey}`),
  }));

  // Permite mostrar un valor escrito manualmente aunque no esté en las opciones.
  type Opt = { value: string; label: string };
  const valueOption = (options: Opt[], val: string): Opt | null =>
    val ? options.find((o) => o.value === val) || { value: val, label: val } : null;
  const formatCreateLabel = (input: string) =>
    t("providers.createOption", { value: input });

  const toggleBtn = (value: boolean, onClick: () => void, labelOn: string, labelOff: string) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center justify-center gap-2 px-3 h-[42px] rounded-xl border text-sm transition-all ${
        value
          ? "bg-green-100 border-green-400 text-green-700 dark:bg-green-900/30 dark:text-green-400"
          : `${themeClasses.cardBg} ${themeClasses.cardBorder} ${themeClasses.textSecondary}`
      }`}
    >
      <i className={`${value ? "ri-toggle-fill" : "ri-toggle-line"} text-base`}></i>
      {value ? labelOn : labelOff}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl ${themeClasses.bg} shadow-2xl`}>
        <div className={`sticky top-0 z-10 flex items-center justify-between p-6 border-b ${themeClasses.border} ${themeClasses.bg}`}>
          <h2 className={`text-lg font-bold ${themeClasses.text}`}>
            <i className="ri-cloud-line mr-2"></i>
            {isEdit ? t("providers.modal.editTitle") : t("providers.modal.addTitle")}
          </h2>
          <button aria-label={t("general.close", { ns: "common" })} onClick={onClose} className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText}`}>
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
          {/* BASIC INFO */}
          <div>
            <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3 flex items-center gap-1`}>
              <i className="ri-information-line"></i> {t("providers.sections.basicInfo")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
              <div className="sm:col-span-2">
                <label className={labelClass}>
                  {t("providers.fields.name")} <span className="text-red-500">*</span>
                </label>
                <input
                  {...register("name", { required: t("providers.validation.nameRequired") })}
                  className={inputClass}
                  placeholder={t("providers.placeholders.name")}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
                )}
              </div>
              <div>
                <label className={labelClass}>{t("providers.fields.status")}</label>
                {toggleBtn(
                  active,
                  () => setValue("active", !active, { shouldDirty: true }),
                  t("providers.active"),
                  t("providers.inactive"),
                )}
              </div>
            </div>
          </div>

          {/* CONNECTION */}
          <div className={`p-4 rounded-xl ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}>
            <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} mb-3 flex items-center gap-1`}>
              <i className="ri-links-line"></i> {t("providers.sections.connection")}
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>{t("providers.fields.adapter")}</label>
                <Controller
                  name="adapter"
                  control={control}
                  render={({ field }) => (
                    <CreatableSelect
                      options={adapterOptions}
                      styles={selectStyles}
                      placeholder={t("providers.placeholders.selectAdapter")}
                      value={valueOption(adapterOptions, field.value)}
                      onChange={(opt: any) => field.onChange(opt?.value || "")}
                      formatCreateLabel={formatCreateLabel}
                      isSearchable
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                  {t("providers.manualHint")}
                </p>
              </div>
              <div>
                <label className={labelClass}>{t("providers.fields.authStrategy")}</label>
                <Controller
                  name="auth_strategy"
                  control={control}
                  render={({ field }) => (
                    <CreatableSelect
                      options={authStrategyOptions}
                      styles={selectStyles}
                      placeholder={t("providers.placeholders.selectAuthStrategy")}
                      value={valueOption(authStrategyOptions, field.value)}
                      onChange={(opt: any) => field.onChange(opt?.value || "")}
                      formatCreateLabel={formatCreateLabel}
                      isSearchable
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  )}
                />
                <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                  {t("providers.manualHint")}
                </p>
              </div>
              <div>
                <label className={labelClass}>{t("providers.fields.discoveryInterval")}</label>
                <input
                  type="number"
                  min={0}
                  {...register("default_discovery_interval_sec")}
                  className={inputClass}
                  placeholder={t("providers.placeholders.discoveryInterval")}
                />
                <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                  {t("providers.secondsHint")}
                </p>
              </div>
              <div>
                <label className={labelClass}>{t("providers.fields.pollInterval")}</label>
                <input
                  type="number"
                  min={0}
                  {...register("default_poll_interval_sec")}
                  className={inputClass}
                  placeholder={t("providers.placeholders.pollInterval")}
                />
                <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                  {t("providers.secondsHint")}
                </p>
              </div>
              <div>
                <label className={labelClass}>{t("providers.fields.supportsDiscovery")}</label>
                {toggleBtn(
                  supportsDiscovery,
                  () => setValue("supports_discovery", !supportsDiscovery, { shouldDirty: true }),
                  t("providers.yes"),
                  t("providers.no"),
                )}
              </div>
            </div>
          </div>

          {/* ENDPOINTS */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} flex items-center gap-1`}>
                <i className="ri-global-line"></i> {t("providers.sections.endpoints")}
              </h3>
              <button
                type="button"
                onClick={() => endpoints.append({ key: "", value: "" })}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500/10 text-primary-600 dark:text-primary-400 hover:bg-primary-500/20 transition-all text-sm font-medium"
              >
                <i className="ri-add-line"></i>
                {t("providers.actions.addEndpoint")}
              </button>
            </div>

            <div className="mb-3">
              <label className={labelClass}>{t("providers.fields.endpointBase")}</label>
              <input
                {...register("endpoints_base")}
                className={inputClass}
                placeholder={t("providers.placeholders.endpointBase")}
              />
            </div>

            <div className="space-y-2">
              {endpoints.fields.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-end gap-3 p-3 rounded-xl ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}
                >
                  <div className="flex-1 min-w-0">
                    <label className={`block text-xs ${themeClasses.textSecondary} mb-1`}>
                      {t("providers.fields.endpointKey")}
                    </label>
                    <input
                      {...register(`endpoints_extra.${index}.key` as const)}
                      className={inputClass}
                      placeholder={t("providers.placeholders.endpointKey")}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className={`block text-xs ${themeClasses.textSecondary} mb-1`}>
                      {t("providers.fields.endpointValue")}
                    </label>
                    <input
                      {...register(`endpoints_extra.${index}.value` as const)}
                      className={inputClass}
                      placeholder={t("providers.placeholders.endpointValue")}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => endpoints.remove(index)}
                    className="flex items-center justify-center h-[42px] w-[42px] shrink-0 rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all"
                  >
                    <i className="ri-delete-bin-line text-lg"></i>
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* CREDENTIALS SCHEMA */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className={`text-sm font-semibold ${themeClasses.textSecondary} flex items-center gap-1`}>
                <i className="ri-key-2-line"></i> {t("providers.sections.credentials")}
              </h3>
              <button
                type="button"
                onClick={() => creds.append({ field: "", label: "", secret: false })}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-primary-500/10 text-primary-600 dark:text-primary-400 hover:bg-primary-500/20 transition-all text-sm font-medium"
              >
                <i className="ri-add-line"></i>
                {t("providers.actions.addCredential")}
              </button>
            </div>

            {creds.fields.length === 0 && (
              <p className={`text-sm ${themeClasses.textSecondary} text-center py-4`}>
                {t("providers.noCredentials")}
              </p>
            )}

            <div className="space-y-2">
              {creds.fields.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex items-end gap-3 p-3 rounded-xl ${isDarkMode ? "bg-dark-bg" : "bg-gray-50"}`}
                >
                  <div className="flex-1 min-w-0">
                    <label className={`block text-xs ${themeClasses.textSecondary} mb-1`}>
                      {t("providers.fields.credentialField")}
                    </label>
                    <input
                      {...register(`credentials_schema.${index}.field` as const)}
                      className={inputClass}
                      placeholder={t("providers.placeholders.credentialField")}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className={`block text-xs ${themeClasses.textSecondary} mb-1`}>
                      {t("providers.fields.credentialLabel")}
                    </label>
                    <input
                      {...register(`credentials_schema.${index}.label` as const)}
                      className={inputClass}
                      placeholder={t("providers.placeholders.credentialLabel")}
                    />
                  </div>
                  <label className={`flex items-center gap-2 h-[42px] shrink-0 whitespace-nowrap text-xs ${themeClasses.text} cursor-pointer`}>
                    <input
                      type="checkbox"
                      {...register(`credentials_schema.${index}.secret` as const)}
                      className="w-4 h-4 rounded accent-primary-500"
                    />
                    {t("providers.fields.credentialSecret")}
                  </label>
                  <div className={`shrink-0 pl-3 ml-1 border-l ${themeClasses.border}`}>
                    <button
                      type="button"
                      onClick={() => creds.remove(index)}
                      className="flex items-center justify-center h-[42px] w-[42px] rounded-lg text-red-500 hover:bg-red-100 dark:hover:bg-red-900/20 transition-all"
                    >
                      <i className="ri-delete-bin-line text-lg"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SUBMIT */}
          <div className={`sticky bottom-0 flex items-center justify-end gap-3 pt-4 border-t ${themeClasses.border} ${themeClasses.bg}`}>
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2.5 rounded-xl text-sm font-medium ${themeClasses.buttonText} ${themeClasses.buttonHover} transition-all`}
            >
              {t("providers.actions.cancel")}
            </button>
            <button
              type="submit"
              disabled={loadingSubmit || (isEdit && !hasFormChanges())}
              className="px-6 py-2.5 rounded-xl text-sm font-medium bg-primary-500 text-gray-900 hover:bg-primary-600 transition-all shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {loadingSubmit && (
                <div className="w-4 h-4 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin"></div>
              )}
              {isEdit ? t("providers.actions.saveChanges") : t("providers.actions.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
